import { clsx } from "clsx";
import { Cpu, MemoryStick, HardDrive, Server } from "lucide-react";
import type { NodeMetrics } from "@/api/endpoints/monitoring";

interface ResourceGaugesProps {
  nodes: NodeMetrics[];
}

interface GaugeProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}

function Gauge({ label, value, icon, color, bgColor }: GaugeProps) {
  const circumference = 2 * Math.PI * 40;
  const offset = circumference - (value / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative h-24 w-24">
        <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r="40"
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            className="text-gray-800"
          />
          <circle
            cx="50"
            cy="50"
            r="40"
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className={color}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold text-gray-200">
            {value.toFixed(0)}%
          </span>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        <span className={clsx("rounded-lg p-1", bgColor)}>{icon}</span>
        <span className="text-xs text-gray-400">{label}</span>
      </div>
    </div>
  );
}

export function ResourceGauges({ nodes }: ResourceGaugesProps) {
  if (nodes.length === 0) {
    return (
      <div className="py-8 text-center text-gray-500">
        No node metrics available.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {nodes.map((node) => (
        <div
          key={node.node}
          className="rounded-xl border border-gray-800 bg-gray-900/50 p-6"
        >
          <div className="mb-4 flex items-center gap-2">
            <Server className="h-5 w-5 text-gray-400" />
            <h3 className="text-base font-semibold text-gray-200">
              {node.node}
            </h3>
            <span
              className={clsx(
                "rounded-full px-2 py-0.5 text-xs font-medium",
                node.status === "online"
                  ? "bg-green-500/20 text-green-400"
                  : node.status === "offline"
                    ? "bg-red-500/20 text-red-400"
                    : "bg-yellow-500/20 text-yellow-400"
              )}
            >
              {node.status}
            </span>
          </div>

          <div className="flex items-center justify-around">
            <Gauge
              label="CPU"
              value={node.cpu_usage}
              icon={<Cpu className="h-3.5 w-3.5 text-blue-400" />}
              color="text-blue-500"
              bgColor="bg-blue-500/10"
            />
            <Gauge
              label="RAM"
              value={node.ram_usage}
              icon={<MemoryStick className="h-3.5 w-3.5 text-purple-400" />}
              color="text-purple-500"
              bgColor="bg-purple-500/10"
            />
            <Gauge
              label="Disk"
              value={node.disk_usage}
              icon={<HardDrive className="h-3.5 w-3.5 text-green-400" />}
              color="text-green-500"
              bgColor="bg-green-500/10"
            />
          </div>
        </div>
      ))}
    </div>
  );
}
